import { MapPin, MapPinOff } from 'lucide-react';

import { type ServiceAreaCheck } from '../hooks/useOutsideServiceArea';

/**
 * Shown when the selected property falls outside the Kalos service area, or, as
 * a neutral note, when its address could not be placed so the boundary was not
 * checked. Informational only -- it never blocks scheduling, and Service Job /
 * Project stay enabled. Rendered in both the Review step (Step 2) and the
 * Schedule step (Step 3) so it stays visible while the call is being booked.
 */
export const OutsideServiceAreaBanner = ({ milesOutside, unlocatable }: ServiceAreaCheck) => {
  if (unlocatable) {
    return (
      <div className="shrink-0 rounded-md border border-gray-200 bg-gray-50 px-4 py-2.5">
        <div className="flex items-start gap-2 text-gray-700">
          <MapPinOff className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          <div className="text-xs leading-relaxed">
            Couldn&apos;t locate this address, so the residential service area wasn&apos;t checked.
          </div>
        </div>
      </div>
    );
  }

  if (milesOutside <= 0) return null;

  // A property just over the line rounds to 0, and "0 mi outside" reads as a bug.
  const distance = milesOutside < 1 ? 'less than 1' : Math.round(milesOutside);

  return (
    <div className="shrink-0 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5">
      <div className="flex items-start gap-2 text-amber-900">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div className="text-xs leading-relaxed">
          <div className="font-semibold">Outside service area</div>
          <div>
            This address is {distance} mi outside the Kalos residential service area. Check with a
            manager before scheduling a residential job.
          </div>
        </div>
      </div>
    </div>
  );
};
